import { useEffect, useId, useMemo, useState, type MouseEvent } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilState } from "recoil";

import { useDropdownSourceContext } from "@ryde/redux";
import { RidesKanbanSortEnum, ridesKanbanSortSelector } from "@ryde/shared";

import {
  RIDES_KANBAN_SORT_LABEL_KEYS,
  type IRidesKanbanSortMenuOption,
} from "./rides-kanban-sort-dropdown.models";

export const useRidesKanbanSortDropdown = () => {
  const { t } = useTranslation();
  const { getDropdownItems } = useDropdownSourceContext();
  const [sort, setSort] = useRecoilState(ridesKanbanSortSelector);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const buttonId = useId();
  const menuId = useId();

  useEffect(() => {
    if (sort !== RidesKanbanSortEnum.AreaAsc) {
      return;
    }

    // Area order reads the area dropdown list. Cards load that list too, but only once a card with areas is
    // rendered, so load it here as well. The provider caches it, so this costs nothing once it is loaded.
    const loadAreaDropdownItems = async (): Promise<void> => {
      await getDropdownItems("area");
    };

    loadAreaDropdownItems();
  }, [getDropdownItems, sort]);

  const openMenuHandler = useEvent(
    (event: MouseEvent<HTMLButtonElement>): void => {
      setMenuAnchor(event.currentTarget);
    },
  );

  const closeMenuHandler = useEvent((): void => {
    setMenuAnchor(null);
  });

  const selectSortHandler = useEvent(
    (selectedSort: RidesKanbanSortEnum): void => {
      setSort(selectedSort);
      setMenuAnchor(null);
    },
  );

  const menuOptions = useMemo(
    (): IRidesKanbanSortMenuOption[] =>
      Object.values(RidesKanbanSortEnum).map(option => ({
        sort: option,
        label: t(RIDES_KANBAN_SORT_LABEL_KEYS[option].option),
        isSelected: option === sort,
        selectHandler: () => selectSortHandler(option),
      })),
    [selectSortHandler, sort, t],
  );

  const menuSlotProps = useMemo(
    // eslint-disable-next-line @typescript-eslint/naming-convention -- ARIA attribute name
    () => ({ list: { "aria-labelledby": buttonId } }),
    [buttonId],
  );

  return {
    title: t("ride.KANBAN_SORT.TITLE"),
    selectedLabel: t(RIDES_KANBAN_SORT_LABEL_KEYS[sort].selected),
    menuOptions,
    menuAnchor,
    isMenuOpen: !!menuAnchor,
    buttonId,
    menuId,
    menuSlotProps,
    openMenuHandler,
    closeMenuHandler,
  };
};
